import { v4 as uuid } from 'uuid';
import Core from '../../Core/Core';
import getLogger, { ILogger } from '../../Core/Logger';
import { DDNAEvents, Tracker } from '../../DeltaTracking/Tracker';
import { ISGTrackData, loadTime } from '../../SDK/SDK';
import { Actions } from '../../State/Actions/Actions';
import { IGameState } from '../../State/Store/store';
import { Storage } from '../../Utils/Storage';

type TrackData = IFBTrackData | ISGTrackData;

const logger: ILogger = getLogger('[TRACKING]');

export interface IFBTrackData {
    eventName: string;
    valueToSum?: number;
    params?: any;
}

export enum IAdStages {
    REQUESTED = 'Requested',
    DELIVERED = 'Displayed',
    FAILED = 'Failed',
}

export enum LoadStages {
    GAME = 'game',
    MAP = 'map',
    FTUE = 'ftue',
}

export abstract class Tracking {
    public static addTrack(data: TrackData, store?: boolean) {
        if (sgPlatform === 'fbinstant') {
            const trackData = data as ISGTrackData;
            if (trackData.dimension1) {
                Core.sdk.platform.track({
                    eventName: 'Design_' + trackData.dimension1,
                    valueToSum: trackData.value || 0,
                    params: trackData.value ? { value: trackData.value } : {},
                });
                if (store) {
                    Core.store.dispatch(Actions.gameTracking(trackData.dimension1));
                }
            } else {
                const trackDataFb = data as IFBTrackData;
                Core.sdk.platform.track(trackDataFb);
            }
        } else if (sgPlatform === 'local') {
        }

        logger.debug(['GAME TRACKING: ', data]);
    }

    public static async setUpTracking(): Promise<boolean> {
        Core.delta = new Tracker();

        const ep = await Core.sdk.getEntryPointData();
        Core.delta.setEntryParams({ entryPoint: ep });

        await Core.delta.initSavedParams();

        const trackUserId = await initTrackingPlayer();
        Core.delta.setSessionParams({
            userID: trackUserId,
            platformUserID: Core.sdk.user.userId,
            loadingTime: loadTime,
            locale: sgPlatform === 'fbinstant' ? FBInstant.getLocale() : 'locale',
        });

        const isNewDevice = await initTrackingDevice();

        const storedTrackingEvents = await Core.sdk.data.getData(Core.delta.queueSaveKey);
        if (storedTrackingEvents && Core.delta) {
            Core.delta.initQueue(storedTrackingEvents);
        }

        return isNewDevice;
    }

    public static startTracking(isNewDevice: boolean, isNewPlayer: boolean) {
        if (Core.delta) {
            if (isNewDevice) {
                Core.delta.addTrackedEvent(DDNAEvents.CLIENT_DEVICE);
            }

            Core.delta.addTrackedEvent(DDNAEvents.GAME_STARTED);
            if (isNewPlayer) {
                Core.delta.addTrackedEvent(DDNAEvents.NEW_PLAYER);
            }
        }

        this.initialLoadComplete(Core.store.getState());
    }

    public static initialLoadComplete(state: IGameState) {
        if (!state.StateTracking.tracked.includes('FTUE_02_LoadingIsCompleted')) {
            this.addTrack(
                {
                    event: 'Design',
                    dimension1: 'FTUE_02_LoadingIsCompleted',
                },
                true
            );

            if (Core.delta) {
                Core.delta.addTrackedEvent(DDNAEvents.FTUX);
            }
        }
    }

    public static trackLoadTime(stage: LoadStages, time: number) {
        const eventId = stage + '_loadtime';
        this.addTrack({
            event: 'Design',
            dimension1: eventId,
            value: time,
        });

        console.log(stage.toUpperCase() + ' LOAD TIME ', time);
    }

    public static trackMissionComplete() {
        if (Core.delta) {
            Core.delta.addTrackedEvent(DDNAEvents.MISSION_COMPLETED);
        }
    }
}

async function initTrackingPlayer(): Promise<string> {
    const savedTrackingUserKey = 'delta-user-id';
    let trackingUserId = await Core.sdk.data.getData(savedTrackingUserKey);
    if (!trackingUserId) {
        trackingUserId = uuid();
        Core.sdk.data.saveData(savedTrackingUserKey, trackingUserId);
    }

    return trackingUserId;
}

async function initTrackingDevice(): Promise<boolean> {
    let isNewDevice: boolean = false;
    const savedDeviceKey = 'moji-device-id';
    const deviceKey = await Storage.getFromStorage(savedDeviceKey);
    if (!deviceKey) {
        const newDeviceId = uuid();
        Storage.saveToStorage(savedDeviceKey, newDeviceId);
        isNewDevice = true;
    }

    return isNewDevice;
}
